import React, { useState, useRef } from 'react';
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import * as api from '../../services/api';

interface Props {
  onClose: () => void;
  onUploaded: () => void;
}

const ReleaseUploadModal: React.FC<Props> = ({ onClose, onUploaded }) => {
  const [version, setVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [rolloutPercentage, setRolloutPercentage] = useState(100);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!version.match(/^\d+\.\d+\.\d+/)) {
      setError('Version must be in semver format (e.g. 1.2.3)');
      return;
    }
    if (!file) {
      setError('Please select a binary file (.zip)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('version', version);
      formData.append('binary', file);
      formData.append('releaseNotes', releaseNotes);
      formData.append('isMandatory', String(isMandatory));
      formData.append('rolloutPercentage', String(rolloutPercentage));

      await api.publishRelease(formData);
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a2e] border border-white/[0.08] rounded-xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-lg font-medium text-white">Publish New Release</h2>
          <button onClick={onClose} className="text-anchor-muted hover:text-white transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Version */}
          <div>
            <label className="block text-sm text-anchor-muted mb-1.5">Version</label>
            <input
              type="text"
              placeholder="1.0.0"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50"
            />
          </div>

          {/* File */}
          <div>
            <label className="block text-sm text-anchor-muted mb-1.5">Binary (.zip)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 bg-white/[0.04] border border-dashed border-white/[0.12] rounded-lg px-3 py-3 text-sm text-anchor-muted hover:border-anchor-blue/40 hover:text-white transition-colors"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {file ? file.name : 'Choose file...'}
            </button>
          </div>

          {/* Release Notes */}
          <div>
            <label className="block text-sm text-anchor-muted mb-1.5">Release Notes</label>
            <textarea
              rows={3}
              placeholder="What changed in this release..."
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50 resize-none"
            />
          </div>

          {/* Mandatory */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isMandatory}
              onChange={(e) => setIsMandatory(e.target.checked)}
              className="accent-anchor-blue"
            />
            <span className="text-sm text-white">Mandatory update</span>
          </label>

          {/* Rollout Percentage */}
          <div>
            <label className="block text-sm text-anchor-muted mb-1.5">
              Rollout Percentage: <span className="text-white">{rolloutPercentage}%</span>
            </label>
            <input
              type="range"
              min={0}
              max={100}
              value={rolloutPercentage}
              onChange={(e) => setRolloutPercentage(Number(e.target.value))}
              className="w-full accent-anchor-blue"
            />
            <div className="flex justify-between text-xs text-anchor-muted mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm text-anchor-muted bg-white/[0.04] border border-white/[0.08] rounded-lg hover:bg-white/[0.06] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-4 py-2.5 text-sm text-white bg-anchor-blue rounded-lg hover:bg-anchor-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Publishing...' : 'Publish Release'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReleaseUploadModal;
